// ══════════════════════════════════════════════════════
// Strategy Lab — Verification Panel (TV Parameter Export)
// ══════════════════════════════════════════════════════

import { useState } from "react";
import { theme, fonts } from "../../theme";
import { Card, CardHeader, Button, Pill } from "../../components/ui";
import { toast } from "../../components/shared";

/* ── Pine Script Parameter Mapping ─────────────── */
/* FIX H-R3-1: Complete coverage of all params engine_fast._pack_params reads.
 * Previously ~30 fields — ~20 were silently missing (be_mode, trail_mode,
 * safety_floor_*, ctx/entry_ema*_length, fvg_proximity, prop_firm policy, etc.)
 * Verify-button gave false confidence. Now mirrors the full YAML schema. */
const PARAM_MAP = [
  { section: "📊 Timeframes", params: [
    { yaml: "timeframes.entry", pine: "Entry Timeframe", key: "entry_tf" },
    { yaml: "timeframes.context", pine: "Context Timeframe", key: "ctx_tf" },
    { yaml: "timeframes.regime", pine: "Regime Timeframe", key: "regime_tf" },
  ]},
  { section: "🔧 Structure", params: [
    { yaml: "structure.entry_mode", pine: "Entry Pattern Mode", key: "entry_mode" },
    { yaml: "structure.break_confirm", pine: "Break Confirm", key: "break_confirm" },
    { yaml: "structure.entry_swing_length", pine: "Entry Swing Length", key: "entry_swing" },
    { yaml: "structure.context_swing_length", pine: "Context Swing Length", key: "ctx_swing" },
    { yaml: "structure.max_pattern_age", pine: "Max Pattern Age (bars)", key: "pattern_age" },
    { yaml: "structure.context_choch_max_age", pine: "Context CHoCH Max Age", key: "choch_age" },
    { yaml: "structure.require_htf_choch", pine: "Require HTF CHoCH", key: "htf_choch" },
    { yaml: "structure.require_htf_trend", pine: "Require HTF Trend", key: "htf_trend" },
    { yaml: "structure.direction_filter", pine: "Direction Filter", key: "direction_filter" },
  ]},
  { section: "🛡️ Stop Loss", params: [
    { yaml: "stop_loss.mode", pine: "SL Mode", key: "sl_mode" },
    { yaml: "stop_loss.atr_length", pine: "ATR Length", key: "atr_len" },
    { yaml: "stop_loss.atr_mult_min", pine: "SL ATR Min Multiplier", key: "atr_min" },
    { yaml: "stop_loss.atr_mult_max", pine: "SL ATR Max Multiplier", key: "atr_max" },
    { yaml: "stop_loss.fixed_points", pine: "SL Fixed Points", key: "sl_fixed_pts" },
    { yaml: "stop_loss.ema_length", pine: "SL EMA Length", key: "sl_ema_len" },
    { yaml: "stop_loss.safety_floor_enabled", pine: "Safety Floor", key: "sf_en" },
    { yaml: "stop_loss.safety_floor_atr", pine: "Floor ATR Multiplier", key: "sf_atr" },
    { yaml: "stop_loss.safety_floor_ticks", pine: "Floor Min Ticks", key: "sf_ticks" },
  ]},
  { section: "🎯 Take Profit", params: [
    { yaml: "take_profit.mode", pine: "TP Mode", key: "tp_mode" },
    { yaml: "take_profit.num_levels", pine: "Number of TP Levels", key: "num_tp_levels" },
    { yaml: "take_profit.partial_trail_mode", pine: "Partial Trail Mode", key: "p_trail_mode" },
    { yaml: "take_profit.trail_after_tp", pine: "Trail starts after TP #", key: "trail_after_tp" },
    { yaml: "take_profit.rr1", pine: "TP1 Risk:Reward", key: "rr1" },
    { yaml: "take_profit.rr2", pine: "TP2 Risk:Reward", key: "rr2" },
    { yaml: "take_profit.rr3", pine: "TP3 Risk:Reward", key: "rr3" },
    { yaml: "take_profit.rr4", pine: "TP4 Risk:Reward", key: "rr4" },
    { yaml: "take_profit.partial1_pct", pine: "Partial 1 %", key: "p1" },
    { yaml: "take_profit.partial2_pct", pine: "Partial 2 %", key: "p2" },
    { yaml: "take_profit.partial3_pct", pine: "Partial 3 %", key: "p3" },
    { yaml: "take_profit.partial4_pct", pine: "Partial 4 %", key: "p4" },
  ]},
  { section: "⚖️ Break Even", params: [
    { yaml: "break_even.enabled", pine: "Break Even Enabled", key: "be" },
    { yaml: "break_even.mode", pine: "BE Mode", key: "be_mode" },
    { yaml: "break_even.after_rr", pine: "BE Trigger RR", key: "be_rr" },
    { yaml: "break_even.fixed_points", pine: "BE Fixed Points", key: "be_fp" },
    { yaml: "break_even.offset_ticks", pine: "BE Offset Ticks", key: "be_offset" },
  ]},
  { section: "📈 Trailing", params: [
    { yaml: "trailing.mode", pine: "Trailing Method", key: "trail_mode" },
    { yaml: "trailing.atr_mult", pine: "Trail ATR Multiplier", key: "trail_mult" },
    { yaml: "trailing.activation_rr", pine: "Trail Activation RR", key: "trail_rr" },
    { yaml: "trailing.ema_length", pine: "Trail EMA Length", key: "trail_ema_len" },
  ]},
  { section: "📊 Regime Filter", params: [
    { yaml: "regime_filter.enabled", pine: "Regime Filter", key: "regime" },
    { yaml: "regime_filter.mode", pine: "Regime Mode", key: "regime_mode" },
    { yaml: "regime_filter.adx_threshold", pine: "ADX Threshold", key: "adx" },
    { yaml: "regime_filter.adx_length", pine: "ADX Length", key: "adx_len" },
    { yaml: "regime_filter.atr_length", pine: "Regime ATR Length", key: "regime_atr_len" },
    { yaml: "regime_filter.atr_sma_length", pine: "ATR SMA Length", key: "sma_len" },
  ]},
  { section: "📉 EMA Filter", params: [
    { yaml: "ema_filter.enabled", pine: "EMA Filter", key: "ema" },
    { yaml: "ema_filter.mode", pine: "EMA Filter Mode", key: "ema_mode" },
    { yaml: "ema_filter.ctx_ema1_length", pine: "CTX EMA 1 Length", key: "ctx_ema1_len" },
    { yaml: "ema_filter.entry_ema1_length", pine: "Entry EMA 1 Length", key: "entry_ema1_len" },
    { yaml: "ema_filter.ema2_length", pine: "EMA 2 Length", key: "ema2_len" },
    { yaml: "ema_filter.ema3_length", pine: "EMA 3 Length", key: "ema3_len" },
  ]},
  { section: "🔍 FVG", params: [
    { yaml: "fvg.enabled", pine: "FVG Filter", key: "fvg" },
    { yaml: "fvg.max_age", pine: "Max FVG Age (bars)", key: "fvg_max_age" },
    { yaml: "entry.fvg_proximity_enabled", pine: "FVG Proximity", key: "fvg_prox" },
  ]},
  { section: "⚠️ Risk Management", params: [
    { yaml: "risk.risk_pct", pine: "Risk Per Trade %", key: "risk_pct" },
    { yaml: "risk.account_size", pine: "Account Size $", key: "account" },
    { yaml: "risk.max_daily_trades", pine: "Max Daily Trades", key: "max_daily" },
    { yaml: "risk.cooldown_bars", pine: "Cooldown Bars", key: "cooldown" },
    { yaml: "risk.max_bars_in_trade", pine: "Max Bars per Trade", key: "max_bars" },
    { yaml: "risk.max_daily_loss_pct", pine: "Max Daily Loss %", key: "max_dl_pct" },
    { yaml: "risk.profit_lock_threshold_pct", pine: "Profit Lock Threshold %", key: "pl_pct" },
    { yaml: "risk.profit_lock_factor", pine: "Profit Lock Factor", key: "pl_factor" },
  ]},
  { section: "📉 Loss Scaling", params: [
  ]},
  { section: "⏰ Bad Hours", params: [
    { yaml: "bad_hours.enabled", pine: "Bad Hours Filter", key: "bad_en" },
    { yaml: "bad_hours.hours", pine: "Blocked Hours", key: "bad_hours" },
  ]},
];

/* ── Helper: Extract value from nested YAML config ── */
function getNestedValue(config, path) {
  const parts = path.split(".");
  let val = config;
  for (const p of parts) {
    if (val == null) return undefined;
    val = val[p];
  }
  return val;
}

function formatValue(val) {
  if (val === true) return "✓ an";
  if (val === false) return "✗ aus";
  if (Array.isArray(val)) return val.length > 0 ? val.join(", ") : "—";
  if (typeof val === "number") return val % 1 === 0 ? String(val) : val.toFixed(2);
  if (val == null) return "—";
  return String(val);
}

/* ═══════════════════════════════════════════════════
   VERIFICATION PANEL
   ═══════════════════════════════════════════════════ */
export default function VerificationPanel({ config, stratFile, onClose }) {
  const [step, setStep] = useState(1);

  if (!config) return null;

  // Detect which sections are inactive based on toggles
  const isSectionInactive = (sectionName) => {
    if (sectionName.includes("Break Even") && config.break_even?.enabled === false) return true;
    if (sectionName.includes("Trailing") && config.trailing?.enabled === false) return true;
    if (sectionName.includes("Regime Filter") && config.regime_filter?.enabled === false) return true;
    if (sectionName.includes("EMA Filter") && config.ema_filter?.enabled === false) return true;
    if (sectionName.includes("FVG") && config.fvg?.enabled === false) return true;
    if (sectionName.includes("Loss Scaling") && config.loss_scaling?.enabled === false) return true;
    if (sectionName.includes("Bad Hours") && config.bad_hours?.enabled === false) return true;
    return false;
  };

  const copyAllParams = () => {
    const lines = [];
    PARAM_MAP.forEach(section => {
      if (isSectionInactive(section.section)) return;
      lines.push(`\n${section.section}`);
      section.params.forEach(p => {
        const val = getNestedValue(config, p.yaml);
        if (val !== undefined) lines.push(`  ${p.pine}: ${formatValue(val)}`);
      });
    });
    navigator.clipboard?.writeText(lines.join("\n")).then(
      () => toast("Parameter in Zwischenablage kopiert!"),
      () => toast("Kopieren fehlgeschlagen", "error")
    );
  };

  const copySection = (section) => {
    const lines = [section.section];
    section.params.forEach(p => {
      const val = getNestedValue(config, p.yaml);
      if (val !== undefined) lines.push(`  ${p.pine}: ${formatValue(val)}`);
    });
    navigator.clipboard?.writeText(lines.join("\n")).then(
      () => toast(`${section.section.replace(/^[^\s]+ /,"")} kopiert!`),
      () => toast("Kopieren fehlgeschlagen", "error")
    );
  };

  return <Card style={{ marginBottom:18, border:`2px solid ${theme.blue}` }} className="sl-fadein">
    <CardHeader
      title={<span style={{ fontSize:16 }}>🔍 Verifikation — {stratFile || "Strategie"}</span>}
      sub="Parameter für TradingView + Schritt-für-Schritt Prüfung"
      right={<div style={{ display:"flex", gap:6 }}>
        <Button onClick={copyAllParams}>📋 Alle kopieren</Button>
        {onClose && <Button onClick={onClose}>Schließen</Button>}
      </div>}
    />

    {/* Step Navigation */}
    <div style={{ display:"flex", gap:4, padding:"12px 20px", borderBottom:`1px solid ${theme.divider}` }}>
      {[{n:1,l:"Parameter"},{n:2,l:"TradingView Setup"},{n:3,l:"Verifikation"},{n:4,l:"Live-Abgleich"}].map(s => (
        <button key={s.n} onClick={() => setStep(s.n)} style={{
          padding:"6px 16px", borderRadius:8, border:"none", cursor:"pointer",
          fontSize:13, fontWeight:step===s.n?700:500,
          background:step===s.n?theme.blue:"transparent",
          color:step===s.n?"#fff":theme.text3,
        }}>{s.n}. {s.l}</button>
      ))}
    </div>

    <div style={{ padding:"18px 22px" }}>
      {/* ── Step 1: All Parameters ── */}
      {step === 1 && <>
        <div style={{ fontSize:13, color:theme.text3, marginBottom:16, lineHeight:1.6 }}>
          Alle optimierten Parameter — übertrage sie in die TradingView Pine Script Einstellungen.
          Graue Sektionen sind deaktiviert (zugehöriger Toggle ist aus) und müssen nicht konfiguriert werden.
        </div>
        {PARAM_MAP.map(section => {
          const visibleParams = section.params.filter(p => getNestedValue(config, p.yaml) !== undefined);
          if (!visibleParams.length) return null;
          const inactive = isSectionInactive(section.section);
          return <div key={section.section} style={{ marginBottom:16, opacity:inactive?0.45:1 }}>
            <div style={{ fontSize:14, fontWeight:800, color:theme.text, marginBottom:8, paddingBottom:6, borderBottom:`1px solid ${theme.divider}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                {section.section}
                {inactive && <span style={{ marginLeft:8, fontSize:10, fontWeight:700, color:theme.text4, background:theme.tint, padding:"2px 8px", borderRadius:4 }}>INAKTIV</span>}
              </div>
              {!inactive && <button onClick={() => copySection(section)} style={{ fontSize:10, padding:"3px 10px", borderRadius:5, border:`1px solid ${theme.border}`, background:theme.white, color:theme.text3, cursor:"pointer", fontWeight:600 }}>📋 Kopieren</button>}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:0 }}>
              {visibleParams.map(p => {
                const val = getNestedValue(config, p.yaml);
                const isBool = typeof val === "boolean";
                return <div key={p.yaml} style={{
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"8px 12px", borderBottom:`1px solid ${theme.divider}`,
                }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:theme.text2 }}>{p.pine}</div>
                    <div style={{ fontSize:10, color:theme.text4, fontFamily:fonts.mono }}>{p.yaml}</div>
                  </div>
                  <div style={{
                    fontSize:14, fontWeight:800, fontFamily:fonts.mono,
                    color: isBool ? (val ? theme.greenText : theme.redText) : theme.text,
                    background: isBool ? (val ? theme.greenLight : theme.redLight) : theme.tint,
                    padding:"4px 10px", borderRadius:6,
                  }}>
                    {formatValue(val)}
                  </div>
                </div>;
              })}
            </div>
          </div>;
        })}
      </>}

      {/* ── Step 2: TradingView Setup ── */}
      {step === 2 && <>
        <div style={{ fontSize:15, fontWeight:800, color:theme.text, marginBottom:14 }}>TradingView Setup — Schritt für Schritt</div>
        {[
          { n:1, title:"Pine Script laden", desc:`Öffne TradingView → Pine Editor → Lade das SMC MTF Strategy Script (v3.7.5)` },
          { n:2, title:"Chart konfigurieren", desc:`Instrument: ${config.instrument?.symbol||"?"} · Exchange: ${config.instrument?.exchange||"?"} · Entry Timeframe: ${config.timeframes?.entry||"?"}` },
          { n:3, title:"Strategy Settings öffnen", desc:`Klicke auf das Zahnrad-Symbol des Indikators → Tab "Inputs"` },
          { n:4, title:"Timeframes setzen", desc:`Entry: ${config.timeframes?.entry||"?"} · Context: ${config.timeframes?.context||"?"} · Regime: ${config.timeframes?.regime||"?"}` },
          { n:5, title:"Structure Parameter", desc:`Entry Mode: ${config.structure?.entry_mode||"?"} · Break: ${config.structure?.break_confirm||"?"} · Swing Entry: ${config.structure?.entry_swing_length||"?"} · Swing Context: ${config.structure?.context_swing_length||"?"}` },
          { n:6, title:"SL/TP Parameter", desc:`SL: ${config.stop_loss?.mode||"?"} · ATR: ${config.stop_loss?.atr_length||"?"} · TP1: ${config.take_profit?.rr1||"?"} · TP2: ${config.take_profit?.rr2||"?"} · TP3: ${config.take_profit?.rr3||"?"} · TPs: ${config.take_profit?.num_levels||"?"}` },
          { n:7, title:"Filter setzen", desc:`Regime: ${config.regime_filter?.enabled?"AN":"AUS"} · EMA: ${config.ema_filter?.enabled?"AN":"AUS"} · FVG: ${config.fvg?.enabled?"AN":"AUS"} · BE: ${config.break_even?.enabled?"AN":"AUS"}` },
          { n:8, title:"Risk Parameter", desc:`Risk: ${config.risk?.risk_pct||"?"}% · Account: $${config.risk?.account_size||"?"} · Max Daily: ${config.risk?.max_daily_trades||"?"} · Cooldown: ${config.risk?.cooldown_bars||"?"}` },
          { n:9, title:"Backtester aktivieren", desc:`Properties → Initial Capital: $${config.risk?.account_size||"50000"} · Commission: 0 · Pyramiding: 0 · Slippage: 0` },
          { n:10, title:"Vergleichen", desc:"Strategy Tester öffnen und P&L, Trades, Win-Rate mit dem Bot-Backtest vergleichen. ±5% Abweichung ist normal." },
        ].map(s => (
          <div key={s.n} style={{
            display:"flex", gap:14, padding:"12px 0",
            borderBottom:`1px solid ${theme.divider}`,
          }}>
            <div style={{
              width:28, height:28, borderRadius:8, flexShrink:0,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:13, fontWeight:800, fontFamily:fonts.mono,
              background:theme.blueLight, color:theme.blueText,
            }}>{s.n}</div>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:theme.text, marginBottom:3 }}>{s.title}</div>
              <div style={{ fontSize:12, color:theme.text3, lineHeight:1.5, fontFamily:fonts.mono }}>{s.desc}</div>
            </div>
          </div>
        ))}
      </>}

      {/* ── Step 3: Verification Checklist ── */}
      {step === 3 && <>
        <div style={{ fontSize:15, fontWeight:800, color:theme.text, marginBottom:14 }}>Verifikations-Checkliste</div>
        <div style={{ fontSize:13, color:theme.text3, marginBottom:16 }}>Prüfe ob Bot und TradingView übereinstimmen. Mindestens 1 Woche Paper-Trading bevor Live.</div>
        {[
          { check:"TradingView zeigt gleiche Entry-Signale wie Bot-Backtest", critical:true },
          { check:"SL-Level stimmen überein (±2 Ticks Toleranz)", critical:true },
          { check:"TP-Level stimmen überein", critical:true },
          { check:"Anzahl Trades im Backtest ±10% identisch", critical:true },
          { check:"P&L im Backtest ±15% identisch", critical:false },
          { check:"Monthly Returns zeigen ähnliches Muster", critical:false },
          { check:"Paper-Trading: Bot erkennt Live-Signale korrekt", critical:true },
          { check:"Paper-Trading: Telegram-Benachrichtigungen kommen", critical:false },
          { check:"Paper-Trading: IBKR zeigt korrekte Positionen", critical:true },
          { check:"Paper-Trading: Reconciliation zeigt keine Mismatches", critical:true },
          { check:"1 Woche Paper ohne Fehler → bereit für Live", critical:true },
        ].map((item, i) => (
          <label key={i} style={{
            display:"flex", alignItems:"center", gap:10,
            padding:"10px 12px", borderRadius:8,
            background:i%2===0?theme.tint:"transparent",
            cursor:"pointer",
          }}>
            <input type="checkbox" style={{ width:18, height:18, accentColor:theme.green, flexShrink:0 }}/>
            <span style={{ fontSize:13, color:theme.text2, lineHeight:1.4 }}>
              {item.check}
              {item.critical && <span style={{ fontSize:10, fontWeight:700, color:theme.redText, marginLeft:6 }}>KRITISCH</span>}
            </span>
          </label>
        ))}
        <div style={{ marginTop:16, padding:"12px 16px", borderRadius:8, background:theme.blueLight, borderLeft:`3px solid ${theme.blue}` }}>
          <div style={{ fontSize:13, color:theme.text2, lineHeight:1.6 }}>
            <strong>Nach erfolgreicher Verifikation:</strong> Bot-Config von Paper-Port (4001) auf Live-Port (4002) umstellen und Bot neu starten.
            Der Bot handelt dann mit echtem Geld über dein IBKR-Konto.
          </div>
        </div>
      </>}

      {/* ── Step 4: Live Trade Verification ── */}
      {step === 4 && <>
        <div style={{ fontSize:15, fontWeight:800, color:theme.text, marginBottom:10 }}>Live-Trade Abgleich Bot ↔ TradingView</div>
        <div style={{ fontSize:13, color:theme.text3, marginBottom:18, lineHeight:1.6 }}>
          Nach dem Live-Schalten läuft der Bot parallel zu deinem TradingView Chart. Diese Anleitung zeigt wie du <strong>jeden einzelnen Trade</strong> zwischen Bot und TV abgleichst — essentiell in den ersten Live-Tagen um Fehler zu erkennen.
        </div>

        {/* Verification Matrix */}
        <div style={{ fontSize:14, fontWeight:700, color:theme.text, marginBottom:10 }}>Was prüfen bei jedem Live-Signal?</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:10, marginBottom:20 }}>
          {[
            { icon:"🕐", title:"Entry-Zeitpunkt", desc:"Bot und TV müssen innerhalb derselben M5-Bar triggern. Max 1 Bar Abweichung ist OK (Data-Feed-Delay).", color:theme.blue },
            { icon:"💲", title:"Entry-Preis", desc:"Max 2 Ticks Abweichung. Größer = Slippage oder falscher Preis-Feed.", color:theme.blue },
            { icon:"🛡️", title:"SL-Level", desc:"Muss auf den Tick genau stimmen. Abweichungen hier sind kritisch!", color:theme.redText },
            { icon:"🎯", title:"TP-Levels", desc:"TP1/TP2/TP3 Preise müssen matchen (auf den Tick).", color:theme.greenText },
            { icon:"📉", title:"Position Size", desc:"Anzahl Kontrakte muss gleich sein (abhängig von Risk% und SL-Distance).", color:theme.text },
            { icon:"⏹️", title:"Exit-Zeitpunkt", desc:"Bot und TV müssen beim SL/TP innerhalb derselben Bar exiten.", color:theme.amberText },
          ].map(item => <div key={item.title} style={{ padding:"12px 14px", borderRadius:10, border:`1px solid ${theme.divider}`, background:theme.white }}>
            <div style={{ fontSize:16, marginBottom:4 }}>{item.icon}</div>
            <div style={{ fontSize:13, fontWeight:700, color:item.color, marginBottom:4 }}>{item.title}</div>
            <div style={{ fontSize:11.5, color:theme.text3, lineHeight:1.5 }}>{item.desc}</div>
          </div>)}
        </div>

        {/* Abweichungs-Troubleshooting */}
        <div style={{ fontSize:14, fontWeight:700, color:theme.text, marginBottom:10 }}>Wenn etwas nicht stimmt</div>
        <div style={{ padding:"14px 18px", borderRadius:10, background:theme.redLight, borderLeft:`3px solid ${theme.redText}`, marginBottom:12 }}>
          <div style={{ fontSize:13, fontWeight:700, color:theme.redText, marginBottom:6 }}>🚨 Bot triggert, TV nicht (oder umgekehrt)</div>
          <div style={{ fontSize:12, color:theme.text2, lineHeight:1.6 }}>
            <strong>Ursachen:</strong><br/>
            • Unterschiedlicher Data-Feed (TV nutzt oft CME-Feed, Bot IBKR)<br/>
            • Pine Script Version nicht aktuell (v3.7.5 muss sein)<br/>
            • Parameter weichen ab — zurück zu Step 1 und alle durchgehen<br/>
            <strong>Action:</strong> Screenshot machen, Parameter nochmal vergleichen
          </div>
        </div>

        <div style={{ padding:"14px 18px", borderRadius:10, background:(theme.amberLight||"rgba(251,191,36,0.08)"), borderLeft:`3px solid ${theme.amberText}`, marginBottom:12 }}>
          <div style={{ fontSize:13, fontWeight:700, color:theme.amberText, marginBottom:6 }}>⚠️ SL/TP-Preis weicht &gt;2 Ticks ab</div>
          <div style={{ fontSize:12, color:theme.text2, lineHeight:1.6 }}>
            <strong>Ursachen:</strong><br/>
            • ATR-Berechnung unterschiedlich (Bot nutzt Daily ATR, TV evtl. andere Periode)<br/>
            • Tick-Size falsch in der YAML konfiguriert<br/>
            <strong>Action:</strong> In der YAML <code style={{background:theme.tint,padding:"1px 4px",borderRadius:3}}>instrument.tick_size</code> und <code style={{background:theme.tint,padding:"1px 4px",borderRadius:3}}>stop_loss.atr_length</code> prüfen
          </div>
        </div>

        <div style={{ padding:"14px 18px", borderRadius:10, background:theme.greenLight, borderLeft:`3px solid ${theme.greenText}`, marginBottom:20 }}>
          <div style={{ fontSize:13, fontWeight:700, color:theme.greenText, marginBottom:6 }}>✓ Entry ±2 Ticks, SL/TP exakt, Exit in derselben Bar</div>
          <div style={{ fontSize:12, color:theme.text2, lineHeight:1.6 }}>
            Perfekt — Bot und TV laufen synchron. Nach 5-10 sauberen Trades kannst du das Monitoring lockern.
          </div>
        </div>

        {/* Monitoring-Tools */}
        <div style={{ fontSize:14, fontWeight:700, color:theme.text, marginBottom:10 }}>Hilfreiche Monitoring-Tools</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:10, marginBottom:20 }}>
          {[
            { title:"Bot Journal", desc:"Zeigt jeden Bot-Trade mit Entry/Exit/P&L im Dashboard", nav:"→ Menü: Journal" },
            { title:"Telegram-Benachrichtigungen", desc:"Echtzeit-Alert bei Entry/Exit. Muss aktiviert sein in der YAML.", nav:"bot.telegram_enabled: true" },
            { title:"Reconciliation Service", desc:"Läuft alle 5 Min und vergleicht Bot-Position vs. IBKR-Position", nav:"Auto-Alert bei Mismatch" },
            { title:"Bot Control", desc:"Live-Status aller Strategien mit offenen Positionen", nav:"→ Menü: Bot Control" },
          ].map(item => <div key={item.title} style={{ padding:"12px 14px", borderRadius:8, background:theme.tint }}>
            <div style={{ fontSize:12.5, fontWeight:700, color:theme.text, marginBottom:3 }}>{item.title}</div>
            <div style={{ fontSize:11, color:theme.text3, lineHeight:1.5, marginBottom:4 }}>{item.desc}</div>
            <div style={{ fontSize:10, color:theme.blueText, fontFamily:fonts.mono }}>{item.nav}</div>
          </div>)}
        </div>

        {/* Final */}
        <div style={{ padding:"14px 18px", borderRadius:10, background:theme.blueLight, borderLeft:`3px solid ${theme.blue}` }}>
          <div style={{ fontSize:13, fontWeight:700, color:theme.blueText, marginBottom:6 }}>📊 Empfohlener Live-Workflow</div>
          <div style={{ fontSize:12, color:theme.text2, lineHeight:1.8 }}>
            <strong>Woche 1:</strong> Paper Trading — jeden Trade manuell mit TV abgleichen<br/>
            <strong>Woche 2:</strong> Paper mit reduziertem Risk — Anomalien identifizieren<br/>
            <strong>Woche 3-4:</strong> Live mit minimalem Risk (0.1%) — Real-Money Stress-Test<br/>
            <strong>Ab Monat 2:</strong> Full Risk wenn alles stabil läuft
          </div>
        </div>
      </>}
    </div>
  </Card>;
}
